/**
 * WB4.7 — revision snapshot API client (medium-tier undo).
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type {
  SaveWorkbenchRevisionSnapshotInput,
  WorkbenchRevisionSnapshotRecord,
} from '@shared/business-workbench/workbench-revision-snapshot';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { fetchWithAuth } from './authFetch';

const getBaseUrl = (): string => resolveApiOrigin();

interface RevisionSnapshotEnvelope {
  success?: boolean;
  data?: {
    snapshot?: WorkbenchRevisionSnapshotRecord | null;
  };
}

export async function saveWorkbenchRevisionSnapshotRemote(
  input: SaveWorkbenchRevisionSnapshotInput
): Promise<WorkbenchRevisionSnapshotRecord | null> {
  try {
    const url = `${getBaseUrl()}/api/workspace/workbench-revision-snapshots`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': input.tenantId,
      },
      body: JSON.stringify(input),
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as RevisionSnapshotEnvelope;
    if (!res.ok || body.success === false || !body.data?.snapshot) {
      return null;
    }
    return body.data.snapshot;
  } catch {
    return null;
  }
}

export async function fetchLatestWorkbenchRevisionSnapshotRemote(input: {
  tenantId: string;
  objectId: string;
}): Promise<WorkbenchRevisionSnapshotRecord | null> {
  const normalizedObjectId = input.objectId.trim();
  if (!normalizedObjectId) {
    return null;
  }
  try {
    const params = new URLSearchParams({ objectId: normalizedObjectId });
    const url = `${getBaseUrl()}/api/workspace/workbench-revision-snapshots/latest?${params.toString()}`;
    const res = await fetchWithAuth(url, {
      method: 'GET',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as RevisionSnapshotEnvelope;
    if (!res.ok || body.success === false) {
      return null;
    }
    return body.data?.snapshot ?? null;
  } catch {
    return null;
  }
}

export async function restoreWorkbenchRevisionSnapshotRemote(input: {
  tenantId: string;
  snapshotId: string;
}): Promise<WorkbenchRevisionSnapshotRecord | null> {
  const snapshotId = input.snapshotId.trim();
  if (!snapshotId) {
    return null;
  }
  try {
    const url = `${getBaseUrl()}/api/workspace/workbench-revision-snapshots/${encodeURIComponent(snapshotId)}/restore`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as RevisionSnapshotEnvelope;
    if (!res.ok || body.success === false || !body.data?.snapshot) {
      return null;
    }
    return body.data.snapshot;
  } catch {
    return null;
  }
}
